import { Plug } from "@phosphor-icons/react"
import { COVERAGE } from "@tokenterminal/core/data/coverage"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import { BrowserMockup } from "../../../common/BrowserMockup"
import NicerBlockquote from "../../../common/NicerBlockquote"
import ProductPageHeader from "../../../products/ProductPageHeader"
import ProductPageTemplate from "../../../products/ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  ProductCTASection,
  StatsGrid,
  ThreeFeaturesSection,
} from "../../../products/shared"
import { BinanceIntegration } from "./integrations/BinanceIntegration"

const IntegrationsPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Plug}
          title="Integrations"
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/products/integrations/banner.png`}
          subtitle="Our standardized metrics embedded directly into third-party platforms, from exchanges to research and media platforms."
          ctaButtons={
            <LinkButton size="large" href="/contact" style={{ zIndex: 4 }}>
              Become a partner
            </LinkButton>
          }
          whoWeServeText="Exchanges and media companies who want to display institutional-grade onchain data to their users."
          simpleWhoWeServeText="exchanges and media companies"
        />
      }
      productId="integrations"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip
        logos={productPageLogos.integrations}
        title="Our data is trusted by 250+ million users daily"
      />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <BinanceIntegration />
              </FeatureCard>
            ),
            title: "Seamless data embedding",
            description:
              "Display Token Terminal metrics natively in your UI. No redirects, no separate logins.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <div className="w-[130%]">
                  <BrowserMockup
                    height="auto"
                    showFadeGradients={false}
                    url="coingecko.com"
                    favicon="https://yt3.googleusercontent.com/JJFCdcykh7qxeXqmC8LuodMMS0wuE9Datth2m0fe-hiIMHgm8KKgpvFfrZtXN26j7OGzc6j_gQ=s900-c-k-c0x00ffffff-no-rj"
                  >
                    <img
                      src={`/${BASE_PATH}static/images/marketing/products/partners/coingecko-dark.jpg`}
                      alt="Token Terminal data on CoinGecko"
                      className="w-full h-auto hidden dark:block"
                    />
                    <img
                      src={`/${BASE_PATH}static/images/marketing/products/partners/coingecko-light.jpg`}
                      alt="Token Terminal data on CoinGecko"
                      className="w-full h-auto dark:hidden"
                    />
                  </BrowserMockup>
                </div>
              </FeatureCard>
            ),
            title: "Always accurate, always current",
            description:
              "Data updates automatically. Our team handles methodology, coverage, and quality.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <div className="w-[130%]">
                  <BrowserMockup
                    height="auto"
                    showFadeGradients={false}
                    url="flipside.ai"
                    favicon="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAMAAABEpIrGAAAAOVBMVEVHcEwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAADLcPMfAAAAE3RSTlMAFVnD7tJ+K07f/zcHqPmQIrJrQJoxBAAAAOlJREFUeAF9kweSRCEIRMEsZu9/2C0p/yRxu7KvVSJ8CpU2xjoPF4VILONEnBhvqfyLc6n0Kfv9DzY6pPvH5/bkn6E4ukgjc0VXDTb8wxMHcMNmAqtdcFtwBTEkvJN07WKw6/OsKg3ZMNfLXhNRkQxj4yUnGGJekRtiCUEaD+Dq8xechvJRegWCITyVMy6DZPC7cmVliivI8m1APhiMG9ehHYbdhRmJ2tkshOZ4CCJHdLYbuUF9fywMDOPn23CMHBu8eeokDS1C31wncewR5p6nLC7OroNp19Vjw+j35UVoWsR7/StC//r8D7bUGwslwDmlAAAAAElFTkSuQmCC"
                  >
                    <img
                      src={`/${BASE_PATH}static/images/marketing/products/partners/flipside-dark.png`}
                      alt="Token Terminal data on Flipside"
                      className="w-full h-auto hidden dark:block"
                    />
                    <img
                      src={`/${BASE_PATH}static/images/marketing/products/partners/flipside-light.png`}
                      alt="Token Terminal data on Flipside"
                      className="w-full h-auto dark:hidden"
                    />
                  </BrowserMockup>
                </div>
              </FeatureCard>
            ),
            title: "Flexible implementation",
            description:
              "Embed via API, widgets, or custom integration. We adapt to your technical requirements.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: COVERAGE.chains,
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: COVERAGE.apps,
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: COVERAGE.tokenizedAssets,
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Token Terminal's decoders turn raw onchain chaos into structured, instruction-level insights—giving every builder the deep microstructure transparency once reserved for sophisticated teams. I look forward to seeing how the Solana community uses these powerful new primitives."
          author="Devan Mitchem"
          authorTitle="Google"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-devan-mitchem.png`}
        />
      </div>

      {/* CTA Section */}
      <ProductCTASection
        tiers={[
          {
            id: "partners",
            title: "For Partners",
            features: [
              "Institutional-grade data infrastructure with 99.9% uptime",
              `Comprehensive coverage across ${COVERAGE.chains} blockchains`,
              "Flexible delivery: REST API, data feeds, or custom integrations",
              "Dedicated partnership team and technical support",
            ],
            cta: (
              <LinkButton className="w-full mt-4" size="large" href="/contact">
                Become a partner
              </LinkButton>
            ),
          },
          {
            id: "projects",
            title: "For Projects",
            features: [
              "Wide distribution network reaching 250M+ users",
              "Institutional visibility and credibility",
              "Standardized listings across platforms",
              "Accurate data validated with your team",
            ],
            cta: (
              <LinkButton className="w-full mt-4" size="large" href="/contact" color="success">
                Get listed
              </LinkButton>
            ),
          },
        ]}
      />
    </ProductPageTemplate>
  )
}

export default IntegrationsPage
